import { Menu, X, Phone, Globe } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle language between French and English
  const toggleLanguage = () => {
    setLanguage(language === 'fr' ? 'en' : 'fr');
  };

  // Check if current route is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Navigation items
  const navItems = [
    { path: '/', label: { en: 'Home', fr: 'Accueil' } },
    { path: '/services', label: { en: 'Services', fr: 'Services' } },
    { path: '/about', label: { en: 'About', fr: 'À Propos' } },
    { path: '/contact', label: { en: 'Contact', fr: 'Contact' } }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg py-2' 
        : 'bg-white shadow-md py-2 md:py-3'
    }`}>
      <div className="container mx-auto px-3 sm:px-4">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          
          {/* Logo and Clinic Name */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0 max-w-[70%] sm:max-w-none">
            <img 
              src="/logo.png" 
              alt="Mains Douces Plus" 
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 lg:h-16 lg:w-16 rounded-full object-cover border-2 border-blue-100 flex-shrink-0"
            />
            
            <div className="text-left overflow-hidden">
              {/* Main Title */}
              <h1 className="font-bold text-blue-900 leading-tight">
                <span className="text-sm sm:text-base md:text-lg lg:text-xl">
                  <span className="hidden xs:inline">Clinique </span>
                  <span className="xs:hidden">Clinique </span>
                  <span className="hidden sm:inline">MAINS DOUCES PLUS</span>
                </span>
              </h1>
              
              {/* Desktop Tagline */}
              <p className="hidden sm:block text-[10px] xs:text-xs md:text-sm text-amber-600 font-medium italic truncate">
                "{t('A good diagnosis for a prompt healing!', 'Un bon Diagnostic pour une Prompt Guérison!')}"
              </p>
              
              {/* Mobile Short Tagline */}
              <p className="block sm:hidden text-[8px] xs:text-[10px] text-amber-600 font-medium italic truncate">
                "{t('Prompt healing!', 'Prompt Guérison!')}"
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            <nav className="flex gap-4 xl:gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-medium transition relative group text-sm xl:text-base whitespace-nowrap ${
                    isActive(item.path) 
                      ? 'text-blue-600' 
                      : 'text-blue-900 hover:text-blue-600'
                  }`}
                >
                  {language === 'en' ? item.label.en : item.label.fr}
                  <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-blue-600 transform scale-x-0 transition-transform group-hover:scale-x-100 ${
                    isActive(item.path) ? 'scale-x-100' : ''
                  }`}></span>
                </Link>
              ))}
            </nav>

            {/* Call Button */}
            <a
               href="https://wa.me/237653474614"
                target="_blank"
                rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 xl:px-4 py-2 rounded-lg font-semibold transition transform hover:scale-105 shadow-md whitespace-nowrap text-sm xl:text-base"
            >
              <Phone size={16} className="xl:w-[18px] xl:h-[18px]" />
              <span className="hidden xl:inline">+237 653 474 614</span>
              <span className="xl:hidden">Call</span>
            </a>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 text-amber-900 px-3 xl:px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap text-sm xl:text-base"
            >
              <Globe size={16} className="xl:w-[18px] xl:h-[18px]" />
              <span>{language === 'en' ? 'FR' : 'EN'}</span>
            </button>
          </div>

          {/* Mobile/Tablet Actions */}
          <div className="flex items-center gap-1 sm:gap-2 lg:hidden">
            {/* WhatsApp Button */}
            <a
              href="https://wa.me/237653474614"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white p-2 sm:p-2.5 rounded-lg transition"
              aria-label="Call us"
            >
              <Phone size={18} className="sm:w-5 sm:h-5" />
            </a>
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="bg-amber-100 hover:bg-amber-200 text-amber-900 p-2 sm:p-2.5 rounded-lg transition"
              aria-label="Toggle language"
            >
              <Globe size={18} className="sm:w-5 sm:h-5" />
            </button>

            {/* Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-blue-900 p-2 sm:p-2.5 hover:bg-blue-50 rounded-lg transition ml-1"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X size={20} className="sm:w-6 sm:h-6" />
              ) : (
                <Menu size={20} className="sm:w-6 sm:h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Dropdown Menu */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-3 sm:mt-4 pb-3 sm:pb-4 flex flex-col gap-1 sm:gap-2 border-t border-blue-100 pt-3 sm:pt-4 animate-fadeIn">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg font-medium transition text-sm sm:text-base ${
                  isActive(item.path) 
                    ? 'bg-blue-600 text-white' 
                    : 'text-blue-900 hover:bg-blue-50'
                }`}
              >
                {language === 'en' ? item.label.en : item.label.fr}
              </Link>
            ))}
            
            {/* Mobile Phone Number Display */}
            <div className="mt-2 pt-2 border-t border-blue-100">
              <a
                href="https://wa.me/237653474614"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 py-2"
              >
                <Phone size={16} />
                <span className="text-sm font-medium">+237 653 474 614</span>
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}